import React, { useEffect, useRef, useMemo } from 'react';
import { Box, IconButton, Stack, Divider } from '@mui/material';
import Tooltip from '../LibraryTooltip';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Title as TitleIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * TipTap-based rich editor for Creator mode.
 * - Inline, always-on editing surface.
 * - Syncs to/from markdown; AI-provided markdown is converted to HTML for display.
 */
export default function CreatorRichEditor({
  valueMarkdown = '',
  onChangeMarkdown,
  /** When true, no right border — parent provides the split divider (e.g. resize rail + chat). */
  hideRightDivider = false,
}) {
  const lastPropMarkdownRef = useRef('');
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
    ],
    content: '',
    onUpdate: ({ editor: ed }) => {
      if (!onChangeMarkdown) return;
      const json = ed.getJSON();
      const md = serializeDocToMarkdown(json);
      lastPropMarkdownRef.current = md;
      onChangeMarkdown(md);
    },
  });

  // When upstream markdown changes (e.g., AI updates), refresh the editor.
  useEffect(() => {
    if (!editor) return;
    const incoming = (valueMarkdown || '').trim();
    if (incoming === lastPropMarkdownRef.current) return;
    lastPropMarkdownRef.current = incoming;
    const rawHtml = marked.parse(incoming || '', { async: false });
    const html =
      typeof rawHtml === 'string' ? DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } }) : '';
    editor.commands.setContent(html, false);
  }, [valueMarkdown, editor]);

  const disabled = !editor;

  const isHeadingActive = useMemo(
    () => ({
      1: () => editor?.isActive('heading', { level: 1 }),
      2: () => editor?.isActive('heading', { level: 2 }),
      3: () => editor?.isActive('heading', { level: 3 }),
    }),
    [editor]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: hideRightDivider
          ? 0
          : { xs: 0, md: '1px solid rgba(0, 0, 0, 0.12)' },
        bgcolor: '#ffffff',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Heading 1">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                sx={{
                  p: 0.5,
                  color: isHeadingActive[1]() ? 'primary.main' : 'text.secondary',
                }}
              >
                <TitleIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Heading 2">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                sx={{
                  p: 0.5,
                  color: isHeadingActive[2]() ? 'primary.main' : 'text.secondary',
                }}
              >
                <TitleIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <Tooltip title="Bold">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                sx={{
                  p: 0.5,
                  color: editor?.isActive('bold') ? 'primary.main' : 'text.secondary',
                }}
              >
                <FormatBold fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                sx={{
                  p: 0.5,
                  color: editor?.isActive('italic') ? 'primary.main' : 'text.secondary',
                }}
              >
                <FormatItalic fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
          <Tooltip title="Bullet list">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                sx={{
                  p: 0.5,
                  color: editor?.isActive('bulletList') ? 'primary.main' : 'text.secondary',
                }}
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Numbered list">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                sx={{
                  p: 0.5,
                  color: editor?.isActive('orderedList') ? 'primary.main' : 'text.secondary',
                }}
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Editor surface */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#ffffff',
          ...(hideRightDivider && {
            borderRight: 'none',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }),
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.25)',
          },
          '& .tiptap': {
            outline: 'none',
            minHeight: 200,
            fontSize: '0.95rem',
            lineHeight: 1.6,
          },
          '& .tiptap p': { margin: 0, marginBottom: 1.5 },
          '& .tiptap h1, & .tiptap h2, & .tiptap h3': {
            marginTop: 2,
            marginBottom: 1,
            fontWeight: 600,
          },
          '& .tiptap h1:first-of-type, & .tiptap h2:first-of-type, & .tiptap h3:first-of-type': {
            marginTop: 0,
          },
          '& .tiptap ul, & .tiptap ol': {
            paddingLeft: 2.5,
            marginTop: 0,
            marginBottom: 1.5,
          },
        }}
      >
        <EditorContent editor={editor} className="tiptap" />
      </Box>
    </Box>
  );
}

/**
 * Minimal serializer from TipTap/ProseMirror JSON doc to markdown.
 * Supports paragraphs, headings, bullet and ordered lists, bold and italic.
 */
function serializeDocToMarkdown(doc) {
  if (!doc || !Array.isArray(doc.content)) return '';

  const lines = [];

  const textFromNodes = (nodes = []) => {
    let out = '';
    nodes.forEach((n) => {
      if (n.type === 'text') {
        let t = n.text || '';
        if (Array.isArray(n.marks)) {
          const hasBold = n.marks.some((m) => m.type === 'bold');
          const hasItalic = n.marks.some((m) => m.type === 'italic');
          if (hasBold) t = `**${t}**`;
          if (hasItalic) t = `_${t}_`;
        }
        out += t;
      } else if (n.content) {
        out += textFromNodes(n.content);
      }
    });
    return out;
  };

  doc.content.forEach((node) => {
    switch (node.type) {
      case 'heading': {
        const level = node.attrs?.level || 1;
        const hashes = '#'.repeat(Math.min(level, 6));
        const text = textFromNodes(node.content);
        lines.push(`${hashes} ${text}`, '');
        break;
      }
      case 'paragraph': {
        const text = textFromNodes(node.content);
        if (text.trim().length > 0) {
          lines.push(text, '');
        }
        break;
      }
      case 'bulletList': {
        (node.content || []).forEach((li) => {
          const text = textFromNodes(li.content?.[0]?.content || li.content || []);
          lines.push(`- ${text}`);
        });
        lines.push('');
        break;
      }
      case 'orderedList': {
        let index = 1;
        (node.content || []).forEach((li) => {
          const text = textFromNodes(li.content?.[0]?.content || li.content || []);
          lines.push(`${index}. ${text}`);
          index += 1;
        });
        lines.push('');
        break;
      }
      default:
        break;
    }
  });

  return lines.join('\n').replace(/\n+$/g, '\n').trimEnd();
}

